import {NgOptimizedImage} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {
  DeButtonComponent,
  DeNavbarBrandIconDirective,
  DeNavbarComponent,
  DeSelectComponent,
} from '@docestate/ui-angular';
import {TranslocoDirective} from '@jsverse/transloco';
import {filter, map} from 'rxjs';
import {LanguageFacade} from '../../i18n/language.facade';
import {normalizeLanguage} from '../../i18n/locale.config';
import {CustomerInteractionStoreService} from '../../services/customer-interaction-store.service';
import {ExternalUrlsService} from '../../services/external-urls.service';
import {MessagingPanelService} from '../../services/messaging-panel.service';

@Component({
  selector: 'app-navbar',
  imports: [
    DeButtonComponent,
    DeNavbarBrandIconDirective,
    DeNavbarComponent,
    DeSelectComponent,
    NgOptimizedImage,
    TranslocoDirective,
  ],
  templateUrl: './app-navbar.html',
  styleUrl: './app-navbar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppNavbar {
  private readonly customerInteractionStore = inject(CustomerInteractionStoreService);
  private readonly externalUrls = inject(ExternalUrlsService);
  private readonly messagingPanel = inject(MessagingPanelService);
  private readonly languageFacade = inject(LanguageFacade);
  private readonly router = inject(Router);
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    {initialValue: this.router.url},
  );

  protected readonly title = signal('DocEstate');
  protected readonly menuOpen = signal(false);
  protected readonly messageCount = this.customerInteractionStore.openCustomerInteractionCount;
  protected readonly showDashboardNavbarActions = computed(() => {
    return this.currentUrl().startsWith('/dashboard/orders/');
  });
  protected readonly currentLanguage = computed(() => {
    return this.languageFacade.currentLanguage().split('-')[0];
  });
  protected readonly selectedLanguageShortLabel = computed(() => this.currentLanguage().toUpperCase());
  protected readonly hasMessageUnreadBadge = computed(() => this.messageCount() > 0);
  protected readonly messageUnreadBadgeLabel = computed(() => `${this.messageCount()}`);
  protected readonly languageOptions = computed(() =>
    this.languageFacade.availableLanguages.map((language) => ({
      value: language.id.substring(0, language.id.indexOf('-') > -1 ? language.id.indexOf('-') : language.id.length),
      label: language.nativeLabel,
    }))
  );

  protected changeLanguage(language: string): void {
    const normalizedLanguage = normalizeLanguage(language);
    this.languageFacade.setLanguage(normalizedLanguage);
    this.closeMenu();
  }

  /** Opens the messaging panel on the interaction overview list. */
  protected openMessages(): void {
    this.closeMenu();
    this.messagingPanel.openList();
  }

  protected openNewRequest(): void {
    this.closeMenu();
    window.open(this.externalUrls.newRequestUrl(), '_blank', 'noopener,noreferrer');
  }

  /**
   * Toggles the state of the menu between open and closed.
   */
  protected toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  /**
   * Closes the menu by setting the menu's open state to false.
   */
  protected closeMenu(): void {
    this.menuOpen.set(false);
  }
}
